import { Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription, takeUntil } from 'rxjs';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { AddressService } from '../../../../shared/services/address/address.service';
import { SMEService } from '../../services/sme.service';
import {
  State,
  County,
  City,
  Zipcode,
} from '../../../../shared/models/address';
import { AuthService } from '../../../auth/services/auth.service';
import { CompanyDetails } from '../../../../shared/models/company-details';
import { ComboboxComponent } from '../../../../shared/components/combobox/combobox.component';

export interface WizardFormData {
  isMilitaryVeteran: boolean | undefined;
  isVeteranSpouse: boolean | undefined;
  preferredLanguage: string;
  state: string;
  county: string;
  city: string;
  zipCode: string;
  hasBusiness: 'yes' | 'no' | 'planning' | undefined;
  businessName: string;
  businessAddress: string;
  businessWebsite: string;
  businessType: string;
  businessEstablished: string;
  numberOfEmployees: number | undefined;
  applicationSummary: string;
  interestedProducts: string[];
  // Helper dropdown tracking IDs
  stateId?: number | null;
  countyId?: number | null;
  cityId?: number | null;
}

export interface ProductOption {
  key: string;
  translationKey: string;
}

const LOOKING_FOR_OPTIONS: ProductOption[] = [
  {
    key: 'Loans and Capital Products',
    translationKey:
      'GENERAL_OUTREACH_FORM.INTERESTED_PRODUCTS.LOANS_AND_CAPITAL',
  },
  {
    key: 'Small Business Technical Assistance',
    translationKey:
      'GENERAL_OUTREACH_FORM.INTERESTED_PRODUCTS.TECHNICAL_ASSISTANCE',
  },
  {
    key: 'Business Operational Tools',
    translationKey: 'GENERAL_OUTREACH_FORM.INTERESTED_PRODUCTS.BUSINESS_TOOLS',
  },
  {
    key: 'Commercial Real Estate',
    translationKey:
      'GENERAL_OUTREACH_FORM.INTERESTED_PRODUCTS.COMMERCIAL_REAL_ESTATE',
  },
  {
    key: 'Solar and Energy Efficiency Products',
    translationKey:
      'GENERAL_OUTREACH_FORM.INTERESTED_PRODUCTS.SOLAR_AND_ENERGY',
  },
  {
    key: 'Finding Investors',
    translationKey:
      'GENERAL_OUTREACH_FORM.INTERESTED_PRODUCTS.FINDING_INVESTORS',
  },
];

const PRODUCT_VALUE_MAP: Record<string, string> = {
  'Loans and Capital Products': 'loans_and_capital',
  'Business Operational Tools': 'business_operational_tools',
  'Solar and Energy Efficiency Products': 'solar_and_energy',
  'Finding Investors': 'finding_investors',
  'Commercial Real Estate': 'commercial_real_estate',
  'Small Business Technical Assistance': 'technical_assistance',
  'ineterested in chmaber of commerce': 'chamber_or_networking',
};

@Component({
  selector: 'app-create-profile-wizard',
  templateUrl: './create-profile-wizard.component.html',
  styleUrls: ['./create-profile-wizard.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, ComboboxComponent],
})
export class CreateProfileWizardComponent
  extends DestroyBase
  implements OnInit
{
  @Output() closed = new EventEmitter<void>();
  @Output() profileCreated = new EventEmitter<CompanyDetails>();

  private addressService = inject(AddressService);
  private smeService = inject(SMEService);
  private authService = inject(AuthService);
  private translateService = inject(TranslateService);

  private citiesAndCountiesSub?: Subscription;
  private zipcodesSub?: Subscription;

  currentStep = 1;
  isSubmitting = false;
  submitError = '';
  chamberAnswer: boolean | null = null;

  get totalSteps(): number {
    return this.form.hasBusiness === 'yes' ? 6 : 5;
  }

  get progressSteps(): number[] {
    return Array.from({ length: this.totalSteps }, (_, i) => i + 1);
  }

  // Step options
  lookingForOptions = LOOKING_FOR_OPTIONS;
  languages = [
    {
      value: 'english',
      translationKey: 'GENERAL_OUTREACH_FORM.OPTIONS.ENGLISH',
    },
    {
      value: 'spanish',
      translationKey: 'GENERAL_OUTREACH_FORM.OPTIONS.SPANISH',
    },
  ];

  businessTypeOptions = [
    {
      value: 'sole_proprietorship',
      translationKey:
        'GENERAL_OUTREACH_FORM.BUSINESS_TYPES.SOLE_PROPRIETORSHIP',
    },
    {
      value: 'lp',
      translationKey: 'GENERAL_OUTREACH_FORM.BUSINESS_TYPES.LP',
    },
    {
      value: 'llp',
      translationKey: 'GENERAL_OUTREACH_FORM.BUSINESS_TYPES.LLP',
    },
    {
      value: 'llc',
      translationKey: 'GENERAL_OUTREACH_FORM.BUSINESS_TYPES.LLC',
    },
    {
      value: 'c_corporation',
      translationKey: 'GENERAL_OUTREACH_FORM.BUSINESS_TYPES.C_CORPORATION',
    },
    {
      value: 's_corporation',
      translationKey: 'GENERAL_OUTREACH_FORM.BUSINESS_TYPES.S_CORPORATION',
    },
    {
      value: 'benefit_corporation',
      translationKey:
        'GENERAL_OUTREACH_FORM.BUSINESS_TYPES.BENEFIT_CORPORATION',
    },
    {
      value: 'close_corporation',
      translationKey: 'GENERAL_OUTREACH_FORM.BUSINESS_TYPES.CLOSE_CORPORATION',
    },
    {
      value: 'non_profit_corporation',
      translationKey:
        'GENERAL_OUTREACH_FORM.BUSINESS_TYPES.NON_PROFIT_CORPORATION',
    },
    {
      value: 'cooperative',
      translationKey: 'GENERAL_OUTREACH_FORM.BUSINESS_TYPES.COOPERATIVE',
    },
    {
      value: 'other',
      translationKey: 'GENERAL_OUTREACH_FORM.BUSINESS_TYPES.OTHER',
    },
  ];

  // Address data
  states: State[] = [];
  counties: County[] = [];
  cities: City[] = [];
  zipcodes: Zipcode[] = [];
  isLoadingStates = false;
  isLoadingCounties = false;
  isLoadingZipcodes = false;

  form: WizardFormData = {
    interestedProducts: [],
    hasBusiness: undefined,
    applicationSummary: '',
    stateId: null,
    state: '',
    countyId: null,
    county: '',
    cityId: null,
    city: '',
    zipCode: '',
    businessName: '',
    businessAddress: '',
    businessWebsite: '',
    businessType: '',
    businessEstablished: '',
    numberOfEmployees: undefined,
    preferredLanguage: 'english',
    isMilitaryVeteran: undefined,
    isVeteranSpouse: undefined,
  };

  get stepTitle(): string {
    if (this.currentStep === 1)
      return 'GENERAL_OUTREACH_FORM.FORM_FIELDS.INTERESTED_PRODUCTS';
    if (this.currentStep === 2)
      return 'GENERAL_OUTREACH_FORM.FORM_FIELDS.DO_YOU_HAVE_BUSINESS';
    if (this.currentStep === 3) {
      return this.form.hasBusiness === 'yes'
        ? 'GENERAL_OUTREACH_FORM.FORM_FIELDS.APPLICATION_SUMMARY_BUSINESS'
        : 'GENERAL_OUTREACH_FORM.FORM_FIELDS.APPLICATION_SUMMARY_GENERAL';
    }
    if (this.currentStep === 4) {
      return this.form.hasBusiness === 'yes'
        ? 'GENERAL_OUTREACH_FORM.FORM_FIELDS.WHERE_IS_BUSINESS_LOCATED'
        : 'GENERAL_OUTREACH_FORM.FORM_FIELDS.WHERE_ARE_YOU_LOCATED';
    }
    if (this.form.hasBusiness === 'yes') {
      if (this.currentStep === 5)
        return 'GENERAL_OUTREACH_FORM.FORM_FIELDS.BUSINESS_DETAILS_HEADING';
      if (this.currentStep === 6)
        return 'GENERAL_OUTREACH_FORM.FORM_FIELDS.MORE_DETAILS_HEADING';
    } else {
      if (this.currentStep === 5)
        return 'GENERAL_OUTREACH_FORM.FORM_FIELDS.MORE_DETAILS_HEADING';
    }
    return '';
  }

  get stepSubtitle(): string {
    if (this.currentStep === 1)
      return 'GENERAL_OUTREACH_FORM.FORM_FIELDS.SELECT_ALL_THAT_APPLY';
    if (this.currentStep === 2)
      return 'GENERAL_OUTREACH_FORM.FORM_FIELDS.BUSINESS_STATUS_SUBTITLE';
    if (this.currentStep === 3) {
      return this.form.hasBusiness === 'yes'
        ? 'GENERAL_OUTREACH_FORM.PLACEHOLDERS.APPLICATION_SUMMARY_BUSINESS_SUBTITLE'
        : 'GENERAL_OUTREACH_FORM.PLACEHOLDERS.APPLICATION_SUMMARY_GENERAL_SUBTITLE';
    }
    if (this.currentStep === 4) {
      return this.form.hasBusiness === 'yes'
        ? 'GENERAL_OUTREACH_FORM.FORM_FIELDS.LOCATION_SUBTITLE_BUSINESS'
        : 'GENERAL_OUTREACH_FORM.FORM_FIELDS.LOCATION_SUBTITLE_PERSONAL';
    }
    if (this.form.hasBusiness === 'yes') {
      if (this.currentStep === 5)
        return 'GENERAL_OUTREACH_FORM.FORM_FIELDS.BUSINESS_DETAILS_SUBTITLE';
      if (this.currentStep === 6)
        return 'GENERAL_OUTREACH_FORM.FORM_FIELDS.MORE_DETAILS_SUBTITLE';
    } else {
      if (this.currentStep === 5)
        return 'GENERAL_OUTREACH_FORM.FORM_FIELDS.MORE_DETAILS_SUBTITLE';
    }
    return '';
  }

  formatCityName(name: string): string {
    if (!name) return '';
    return name
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  formatCityItem = (item: City | string | unknown): string => {
    if (!item) return '';
    const name = typeof item === 'string' ? item : (item as City)?.name || '';
    return this.formatCityName(name);
  };

  getStatePlaceholder(): string {
    if (this.isLoadingStates) {
      return this.translateService.instant(
        'GENERAL_OUTREACH_FORM.OPTIONS.LOADING_STATES',
      );
    }
    return this.translateService.instant(
      'GENERAL_OUTREACH_FORM.OPTIONS.SELECT_STATE',
    );
  }

  getCountyPlaceholder(): string {
    if (!this.form.state) {
      return this.translateService.instant(
        'GENERAL_OUTREACH_FORM.OPTIONS.SELECT_STATE_FIRST',
      );
    }
    if (this.isLoadingCounties || this.counties.length === 0) {
      return this.translateService.instant(
        'GENERAL_OUTREACH_FORM.OPTIONS.LOADING_COUNTIES',
      );
    }
    return this.translateService.instant(
      'GENERAL_OUTREACH_FORM.OPTIONS.SELECT_COUNTY',
    );
  }

  getCityPlaceholder(): string {
    if (!this.form.state) {
      return this.translateService.instant(
        'GENERAL_OUTREACH_FORM.OPTIONS.SELECT_STATE_FIRST',
      );
    }
    if (!this.form.county) {
      return this.translateService.instant(
        'GENERAL_OUTREACH_FORM.OPTIONS.SELECT_COUNTY_FIRST',
      );
    }
    if (this.isLoadingCounties || this.cities.length === 0) {
      return this.translateService.instant(
        'GENERAL_OUTREACH_FORM.OPTIONS.LOADING_CITIES',
      );
    }
    return this.translateService.instant(
      'GENERAL_OUTREACH_FORM.OPTIONS.SELECT_CITY',
    );
  }

  getZipPlaceholder(): string {
    if (!this.form.city) {
      return this.translateService.instant(
        'GENERAL_OUTREACH_FORM.OPTIONS.SELECT_CITY_FIRST',
      );
    }
    if (this.isLoadingZipcodes) {
      return this.translateService.instant(
        'GENERAL_OUTREACH_FORM.OPTIONS.LOADING_ZIPCODES',
      );
    }
    return this.translateService.instant(
      'GENERAL_OUTREACH_FORM.OPTIONS.SELECT_ZIP',
    );
  }

  get businessAddressLine2(): string {
    const city = this.form.city ? this.formatCityName(this.form.city) : '';
    const county = this.form.county;
    const state = this.form.state;
    const zipcode = this.form.zipCode;

    const countyStr = county
      ? county.toLowerCase().includes('county')
        ? county
        : `${county} County`
      : '';
    const addressParts = [city, countyStr, state, zipcode].filter(Boolean);
    return addressParts.join(', ');
  }

  get isBusinessEstablishedFormatValid(): boolean {
    const val = String(this.form.businessEstablished ?? '').trim();
    if (!val) return false;
    const yearRegex = /^\d{4}$/;
    const dateRegex = /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12][0-9]|3[01])\/\d{4}$/;
    if (!yearRegex.test(val) && !dateRegex.test(val)) return false;

    if (dateRegex.test(val)) {
      const parts = val.split('/');
      const month = parseInt(parts[0], 10) - 1;
      const day = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      const inputDate = new Date(year, month, day);
      if (
        inputDate.getFullYear() !== year ||
        inputDate.getMonth() !== month ||
        inputDate.getDate() !== day
      ) {
        return false;
      }
    }
    return true;
  }

  get isBusinessEstablishedNotFuture(): boolean {
    const val = String(this.form.businessEstablished ?? '').trim();
    if (!val || !this.isBusinessEstablishedFormatValid) return true;
    const yearRegex = /^\d{4}$/;

    if (yearRegex.test(val)) {
      const year = parseInt(val, 10);
      const currentYear = new Date().getFullYear();
      return year <= currentYear;
    } else {
      const parts = val.split('/');
      const month = parseInt(parts[0], 10) - 1;
      const day = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      const inputDate = new Date(year, month, day);
      const today = new Date();
      const todayStart = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
      );
      return inputDate <= todayStart;
    }
  }

  get isBusinessEstablishedValid(): boolean {
    return (
      this.isBusinessEstablishedFormatValid &&
      this.isBusinessEstablishedNotFuture
    );
  }

  get isWebsiteValid(): boolean {
    const val = this.form.businessWebsite?.trim();
    if (!val) return true;
    const urlRegex =
      /^(https?:\/\/)?([a-zA-Z0-9.-]+)\.([a-zA-Z]{2,6})(\/[a-zA-Z0-9_.-]*)*\/?$/;
    return urlRegex.test(val);
  }

  get isNumberOfEmployeesValid(): boolean {
    const val = this.form.numberOfEmployees;
    if (val === undefined || val === null) return false;
    return val >= 0 && Number.isInteger(Number(val));
  }

  blockDecimalAndSigns(event: KeyboardEvent): void {
    if (['.', 'e', 'E', '-', '+'].includes(event.key)) {
      event.preventDefault();
    }
  }

  blockPasteDecimal(event: ClipboardEvent): void {
    const clipboardData = event.clipboardData;
    if (!clipboardData) return;
    const pastedText = clipboardData.getData('text');
    if (
      pastedText &&
      (pastedText.includes('.') ||
        pastedText.includes('e') ||
        pastedText.includes('E') ||
        pastedText.includes('-') ||
        pastedText.includes('+'))
    ) {
      event.preventDefault();
    }
  }

  get canGoNext(): boolean {
    switch (this.currentStep) {
      case 1:
        return this.form.interestedProducts.length > 0;
      case 2:
        return this.form.hasBusiness !== undefined;
      case 3:
        return this.form.applicationSummary.trim().length > 0;
      case 4:
        if (this.form.hasBusiness === 'yes') {
          return (
            !!this.form.state &&
            !!this.form.county &&
            !!this.form.city &&
            !!this.form.zipCode
          );
        } else {
          return (
            !!this.form.businessAddress?.trim() &&
            !!this.form.state &&
            !!this.form.county &&
            !!this.form.city &&
            !!this.form.zipCode
          );
        }
      case 5:
        if (this.form.hasBusiness === 'yes') {
          return (
            !!this.form.businessName.trim() &&
            !!this.form.businessAddress.trim() &&
            !!this.form.businessType &&
            this.isBusinessEstablishedValid &&
            this.isNumberOfEmployeesValid &&
            this.isWebsiteValid
          );
        } else {
          return (
            !!this.form.preferredLanguage &&
            this.form.isMilitaryVeteran !== undefined &&
            this.form.isVeteranSpouse !== undefined &&
            this.chamberAnswer !== null
          );
        }
      case 6:
        if (this.form.hasBusiness === 'yes') {
          return (
            !!this.form.preferredLanguage &&
            this.form.isMilitaryVeteran !== undefined &&
            this.form.isVeteranSpouse !== undefined &&
            this.chamberAnswer !== null
          );
        }
        return false;
      default:
        return false;
    }
  }

  ngOnInit(): void {
    this.loadStates();
  }

  // ── Step 1 ─────────────────────────────────────────────
  toggleLookingFor(option: string): void {
    const idx = this.form.interestedProducts.indexOf(option);
    if (idx === -1) {
      this.form.interestedProducts.push(option);
    } else {
      this.form.interestedProducts.splice(idx, 1);
    }
  }

  isLookingForSelected(option: string): boolean {
    return this.form.interestedProducts.includes(option);
  }

  // ── Step 5 / 6 Chamber of commerce ──────────────────────
  toggleChamberInterest(interested: boolean): void {
    this.chamberAnswer = interested;
    const chamberString = 'ineterested in chmaber of commerce';
    const index = this.form.interestedProducts.indexOf(chamberString);
    if (interested) {
      if (index === -1) {
        this.form.interestedProducts.push(chamberString);
      }
    } else {
      if (index !== -1) {
        this.form.interestedProducts.splice(index, 1);
      }
    }
  }

  isChamberInterestSelected(interested: boolean): boolean {
    if (this.chamberAnswer === null) return false;
    return this.chamberAnswer === interested;
  }

  // ── Step 4 — Address cascade ───────────────────────────
  private loadStates(): void {
    this.isLoadingStates = true;
    this.addressService
      .getStates()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (states) => {
          this.states = (states || [])
            .slice()
            .sort((a, b) =>
              a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
            );
          this.isLoadingStates = false;
        },
        error: () => {
          this.isLoadingStates = false;
        },
      });
  }

  onStateChange(): void {
    // Reset downstream
    this.form.countyId = null;
    this.form.county = '';
    this.form.cityId = null;
    this.form.city = '';
    this.form.zipCode = '';
    this.counties = [];
    this.cities = [];
    this.zipcodes = [];

    this.citiesAndCountiesSub?.unsubscribe();
    this.zipcodesSub?.unsubscribe();

    if (!this.form.state) {
      this.form.stateId = null;
      return;
    }

    const state = this.states.find((s) => s.name === this.form.state);
    if (!state) return;
    this.form.stateId = state.id;

    this.isLoadingCounties = true;
    this.citiesAndCountiesSub = this.addressService
      .getCitiesAndCountiesByStateIds([state.id])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ counties, cities }) => {
          this.counties = (counties || [])
            .slice()
            .sort((a, b) =>
              a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
            );
          this.cities = (cities || [])
            .slice()
            .sort((a, b) =>
              this.formatCityName(a.name).localeCompare(
                this.formatCityName(b.name),
                undefined,
                { sensitivity: 'base' },
              ),
            );
          this.isLoadingCounties = false;
        },
        error: () => {
          this.isLoadingCounties = false;
        },
      });
  }

  onCountyChange(): void {
    this.form.cityId = null;
    this.form.city = '';
    this.form.zipCode = '';
    this.zipcodes = [];
    this.zipcodesSub?.unsubscribe();

    if (!this.form.county) {
      this.form.countyId = null;
      return;
    }

    const county = this.counties.find((c) => c.name === this.form.county);
    if (!county) return;
    this.form.countyId = county.id;
  }

  onCityChange(): void {
    this.form.zipCode = '';
    this.zipcodes = [];
    this.zipcodesSub?.unsubscribe();

    if (!this.form.city) {
      this.form.cityId = null;
      return;
    }

    const city = this.cities.find((c) => c.name === this.form.city);
    if (!city) {
      this.form.cityId = null;
      return;
    }
    this.form.cityId = city.id;

    this.isLoadingZipcodes = true;
    this.zipcodesSub = this.addressService
      .getZipcodes(city.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (zips) => {
          this.zipcodes = (zips || [])
            .slice()
            .sort((a, b) => a.code.localeCompare(b.code));
          this.isLoadingZipcodes = false;
        },
        error: () => {
          this.isLoadingZipcodes = false;
        },
      });
  }

  // ── Navigation ─────────────────────────────────────────
  next(): void {
    if (!this.canGoNext) return;
    if (this.currentStep < this.totalSteps) {
      this.currentStep++;
    } else {
      this.submit();
    }
  }

  back(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  close(): void {
    this.closed.emit();
  }

  // ── Submit ─────────────────────────────────────────────
  private buildPayload(): Record<string, unknown> {
    const interestedProductsMapped = this.form.interestedProducts.map(
      (prod) => PRODUCT_VALUE_MAP[prod] || prod,
    );

    const userInfo = this.authService.getLoggedInUserInfo();
    const payload: Record<string, unknown> = {
      name: userInfo?.name,
      email: userInfo?.email,
      interested_products: interestedProductsMapped,
      has_business: this.form.hasBusiness,
      application_summary: this.form.applicationSummary,
      business_address: this.form.businessAddress,
      state: this.form.state,
      county: this.form.county,
      city: this.form.city,
      zip_code: this.form.zipCode,
      preferred_language: this.form.preferredLanguage,
      is_military_veteran: this.form.isMilitaryVeteran,
      is_veteran_spouse: this.form.isVeteranSpouse,
      ...(this.form.hasBusiness === 'yes'
        ? {
            business_name: this.form.businessName,
            ...(this.form.businessWebsite?.trim()
              ? { business_website: this.form.businessWebsite.trim() }
              : {}),
            business_type: this.form.businessType,
            business_established: this.form.businessEstablished,
            number_of_employees: this.form.numberOfEmployees,
          }
        : {}),
    };

    return payload;
  }

  submit(): void {
    this.isSubmitting = true;
    this.submitError = '';

    this.smeService
      .createSMEBusinessRecord(this.buildPayload())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (profile) => {
          this.isSubmitting = false;
          this.profileCreated.emit(profile);
        },
        error: (err) => {
          this.isSubmitting = false;
          this.submitError =
            err?.error?.message || 'Something went wrong. Please try again.';
          console.error('Error creating business profile', err);
        },
      });
  }
}
